import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare, Search, RefreshCw, Send, X, ExternalLink, Loader2, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { VoiceScriptPlayer } from "@/components/common/VoiceScriptPlayer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 30;
const CANALES = ["whatsapp", "sms", "voz", "email"];
const ESTADOS = ["encolado", "reintento", "enviado", "entregado", "leido", "fallido"];
export const MSG_TONE = { entregado: "green", leido: "green", enviado: "blue", encolado: "gray",
  reintento: "amber", fallido: "red", cancelado: "gray" };

const MessageSheet = ({ id, onClose, onChanged }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["message", id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/messages/${id}`)).data,
  });

  const msg = data?.message;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/messages/${id}/resend`);
      toast.success(t("messages.resent"));
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!id} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="message-sheet" className="w-full overflow-y-auto sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle className="text-[15px]">{t("messages.detail")}</SheetTitle>
        </SheetHeader>
        {!msg ? (
          <p className="mt-6 text-[13px] text-ink-tertiary">{t("app.loading")}</p>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusChip tone={msg.canal === "voz" ? "amber" : "blue"} label={t(`channels.${msg.canal}`)} />
              <StatusChip tone={MSG_TONE[msg.estado] || "gray"} testId="message-sheet-state"
                label={t(`messages.states.${msg.estado}`)} />
              <span className="text-[12px] text-ink-tertiary">{t(`messages.events.${msg.evento}`)}</span>
            </div>

            <div className="rounded-[8px] border border-line bg-surface-sunken p-3">
              {msg.asunto && <p className="mb-1 text-[12.5px] font-semibold text-ink">{msg.asunto}</p>}
              <p data-testid="message-sheet-body" className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink">
                {msg.cuerpo}
              </p>
              {msg.media_url && (
                <a href={msg.media_url} target="_blank" rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-brand hover:underline">
                  <ExternalLink className="h-3 w-3" /> {t("messages.attachment")}
                </a>
              )}
              {msg.canal === "voz" && (
                <div className="mt-2.5">
                  <VoiceScriptPlayer script={msg.voice_script || msg.cuerpo}
                    label={t("messages.play_script")} testId="message-sheet-play" />
                </div>
              )}
            </div>

            <dl className="rounded-[8px] border border-line bg-white px-3 py-1">
              {[
                [t("messages.to"), `${msg.destinatario_nombre || "—"} · ${msg.destinatario_contacto || "—"}`],
                [t("messages.template"), data.template ? `${data.template.evento} · ${data.template.canal} · ${data.template.idioma}` : "—"],
                [t("messages.attempts"), msg.intentos_count],
                [t("app.status"), t(`messages.states.${msg.estado}`)],
                [t("messages.created"), formatDateTime(msg.created_at)],
                [msg.error ? t("messages.error") : t("messages.provider_ref"), msg.error || msg.provider_ref || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-4 border-b border-line py-2 last:border-0">
                  <dt className="text-[12px] text-ink-tertiary">{k}</dt>
                  <dd className="max-w-[62%] text-right text-[12.5px] text-ink">{v}</dd>
                </div>
              ))}
            </dl>

            {!!msg.intentos?.length && (
              <section className="rounded-[8px] border border-line bg-white" data-testid="message-attempts">
                <header className="border-b border-line px-3 py-2">
                  <h3 className="text-[12.5px] font-semibold text-ink">{t("messages.attempts")}</h3>
                </header>
                <ul>
                  {msg.intentos.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 border-b border-line px-3 py-2 last:border-0">
                      <StatusChip tone={a.resultado === "enviado" ? "green" : a.resultado === "fallido" ? "red" : "gray"}
                        label={t(`channels.${a.canal}`)} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12px] text-ink">{a.detalle || a.resultado}</p>
                        <p className="text-[11px] text-ink-tertiary">{formatDateTime(a.at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="flex flex-wrap gap-2">
              <Button data-testid="message-resend" disabled={busy} onClick={resend}
                className="h-8 rounded-[6px] bg-ink text-[12.5px] hover:bg-ink">
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                {t("messages.resend")}
              </Button>
              {msg.work_order_id && (
                <Button variant="outline" data-testid="message-go-wo"
                  onClick={() => navigate(`/ordenes/${msg.work_order_id}`)}
                  className="h-8 rounded-[6px] text-[12.5px]">
                  {msg.wo_code}
                </Button>
              )}
              {msg.destinatario_tipo === "sub" && msg.destinatario_id && (
                <Button variant="outline" data-testid="message-go-sub"
                  onClick={() => navigate(`/subcontratistas/${msg.destinatario_id}`)}
                  className="h-8 rounded-[6px] text-[12.5px]">
                  {t("messages.see_conversation")}
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default function MessagesPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState(null);

  const get = (k, d = "") => params.get(k) || d;
  const patch = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (!v || v === "all") next.delete(k);
      else next.set(k, v);
    });
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const page = Number(get("page", "1"));
  const filters = {
    canal: get("canal") || undefined,
    estado: get("estado") || undefined,
    q: get("q") || undefined,
    desde: get("desde") || undefined,
    hasta: get("hasta") || undefined,
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["messages", { ...filters, page }],
    refetchInterval: 15000,
    queryFn: async () =>
      (await api.get("/messages", { params: { ...filters, page, page_size: PAGE_SIZE } })).data,
  });

  const refresh = () => {
    refetch();
    qc.invalidateQueries({ queryKey: ["message"] });
  };

  const processNow = async () => {
    try {
      const { data: res } = await api.post("/messages/process");
      toast.success(t("messages.processed", { n: res.enviados || 0 }));
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    }
  };

  const columns = [
    {
      key: "created_at", label: t("messages.when"), width: 150,
      render: (r) => <span className="text-[12.5px] text-ink-secondary">{formatDateTime(r.created_at)}</span>,
    },
    {
      key: "canal", label: t("messages.channel"), width: 120,
      render: (r) => <StatusChip tone={r.canal === "voz" ? "amber" : "blue"} label={t(`channels.${r.canal}`)} />,
    },
    {
      key: "destinatario_nombre", label: t("messages.to"), width: 190,
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-ink">{r.destinatario_nombre || "—"}</p>
          <p className="truncate text-[11.5px] tabular-nums text-ink-tertiary">{r.destinatario_contacto}</p>
        </div>
      ),
    },
    {
      key: "evento", label: t("messages.event"), width: 170,
      render: (r) => <span className="text-[12.5px]">{t(`messages.events.${r.evento}`)}</span>,
    },
    {
      key: "cuerpo", label: t("messages.excerpt"),
      render: (r) => <span className="line-clamp-2 text-[12.5px] text-ink-secondary">{r.cuerpo}</span>,
    },
    {
      key: "wo_code", label: t("wo.code"), width: 130,
      render: (r) => <span className="tabular-nums text-[12.5px]">{r.wo_code || "—"}</span>,
    },
    {
      key: "estado", label: t("app.status"), width: 120,
      render: (r) => (
        <StatusChip tone={MSG_TONE[r.estado] || "gray"} testId={`message-state-${r.id}`}
          label={t(`messages.states.${r.estado}`)} />
      ),
    },
  ];

  const c = data?.contadores || {};

  return (
    <>
      <PageHeader
        title={t("messages.title")}
        subtitle={t("messages.subtitle", { p: data?.provider || "simulator" })}
        testId="messages-header"
        actions={
          <>
            <Button variant="outline" data-testid="messages-process" onClick={processNow}
              className="h-8 rounded-[6px] text-[13px]">
              <Zap className="mr-1.5 h-3.5 w-3.5" /> {t("messages.process_now")}
            </Button>
            <Button variant="outline" data-testid="messages-refresh" onClick={refresh}
              className="h-8 rounded-[6px] text-[13px]">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> {t("messages.refresh")}
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="mr-2 flex items-center gap-1.5" data-testid="messages-counters">
          <StatusChip tone="blue" testId="messages-count-sent" label={`${(c.enviado || 0) + (c.entregado || 0) + (c.leido || 0)} ${t("messages.states.enviado")}`} />
          <StatusChip tone="green" testId="messages-count-delivered" label={`${(c.entregado || 0) + (c.leido || 0)} ${t("messages.states.entregado")}`} />
          <StatusChip tone="red" testId="messages-count-failed" label={`${c.fallido || 0} ${t("messages.states.fallido")}`} />
          {!!(c.encolado || c.reintento) && (
            <StatusChip tone="amber" testId="messages-count-queued" label={`${(c.encolado || 0) + (c.reintento || 0)} ${t("messages.states.encolado")}`} />
          )}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input data-testid="messages-search" defaultValue={get("q")}
            onKeyDown={(e) => e.key === "Enter" && patch({ q: e.currentTarget.value })}
            onBlur={(e) => patch({ q: e.currentTarget.value })}
            placeholder={t("messages.search")} className="h-8 w-[260px] rounded-[6px] pl-8 text-[13px]" />
        </div>
        <Select value={get("canal", "all")} onValueChange={(v) => patch({ canal: v })}>
          <SelectTrigger data-testid="messages-channel-filter" className="h-8 w-[150px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("messages.channel")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("messages.channel")}: {t("app.all")}</SelectItem>
            {CANALES.map((x) => <SelectItem key={x} value={x}>{t(`channels.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={get("estado", "all")} onValueChange={(v) => patch({ estado: v })}>
          <SelectTrigger data-testid="messages-state-filter" className="h-8 w-[150px] rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("app.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.status")}: {t("app.all")}</SelectItem>
            {ESTADOS.map((x) => <SelectItem key={x} value={x}>{t(`messages.states.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" data-testid="messages-from" value={get("desde")}
          onChange={(e) => patch({ desde: e.target.value })}
          className="h-8 w-[140px] rounded-[6px] text-[12.5px]" />
        <Input type="date" data-testid="messages-to" value={get("hasta")}
          onChange={(e) => patch({ hasta: e.target.value })}
          className="h-8 w-[140px] rounded-[6px] text-[12.5px]" />
        {["q", "canal", "estado", "desde", "hasta"].some((k) => params.get(k)) && (
          <Button variant="ghost" data-testid="messages-clear-filters"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="h-8 rounded-[6px] text-[12.5px] text-ink-tertiary">
            <X className="mr-1 h-3.5 w-3.5" /> {t("wo.clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="messages-page">
        <DataTable
          testId="messages-table"
          columns={columns}
          rows={data?.items || []}
          total={data?.total || 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={(p) => patch({ page: String(p) })}
          loading={isLoading}
          onRowClick={(r) => setOpenId(r.id)}
          empty={<EmptyState icon={MessageSquare} testId="messages-empty"
            title={t("messages.empty_title")} description={t("messages.empty_desc")} />}
        />
      </div>

      <MessageSheet id={openId} onClose={() => setOpenId(null)} onChanged={refresh} />
    </>
  );
}
